import { useApolloClient } from '@apollo/client';
import { AiDraftLiveUpdateDocument } from 'lib-knowledge-management-common/graphql';
import { useEffect, useRef } from 'react';

import { useSubscribeToAiDraftEvents } from '../aiDraftEvents';
import {
  placeInQueue,
  removeFromQueue,
  setDraftLocked,
} from '../utils/draftQueueCache';

/** The queue and the preview: what a missed event can have left stale. */
const resyncedQueries = new Set(['AiDrafts', 'AiDraft']);

/**
 * Keeps the queue in step with drafts changed by the AI or by another reviewer,
 * as the host app's web socket reports them.
 *
 * The events carry ids only and never say who acted, so a reviewer's own
 * approve, discard or revision comes back too. Every handler below is written
 * to be run again on a queue that already reflects it, and leaves it as it is.
 *
 * Without the host supplying events this does nothing, and the queue is the
 * snapshot it was before.
 */
export function useAiDraftLiveUpdates({
  onGone,
}: {
  /**
   * A draft left the queue — deleted or approved — however it went.
   * Also called for the echo of the reviewer's own approve or discard.
   */
  onGone: (id: string) => void;
}): void {
  const subscribe = useSubscribeToAiDraftEvents();
  const client = useApolloClient();

  // Read through a ref so a caller passing a fresh closure each render, over
  // whatever it has selected, does not tear the subscription down every time.
  const onGoneRef = useRef(onGone);
  onGoneRef.current = onGone;

  useEffect(() => {
    if (!subscribe) return undefined;

    const { cache } = client;

    const removeGone = (id: string) => {
      removeFromQueue(cache, id);
      onGoneRef.current(id);
    };

    // Drafts deleted while the page is open. The read an upsert starts can
    // answer after a deletion that came later, and would put the draft back.
    const deleted = new Set<string>();

    return subscribe({
      onUpserted: (id) => {
        client
          .query({
            query: AiDraftLiveUpdateDocument,
            variables: { id },
            // The event is the news that the cached copy is stale.
            fetchPolicy: 'network-only',
          })
          .then(({ data }) => {
            const draft = data?.aiDraft;

            // Approving is an upsert too, and takes the draft out of the
            // queue; a discarded one is deleted and reads as nothing. The read
            // itself has already refreshed the entity, so a draft that stays
            // is up to date in its row and in the preview; all that is left
            // is where it sits in the list.
            if (!draft || draft.approvedAt != null) {
              removeGone(id);
            } else if (!deleted.has(id)) {
              placeInQueue(cache, draft);
            }
          })
          // A read that fails leaves the queue as stale as it would have been
          // without the event, which the next reload or reconnect puts right.
          .catch(() => undefined);
      },

      onDeleted: (id) => {
        deleted.add(id);
        removeGone(id);
        cache.evict({ id: cache.identify({ __typename: 'AiDraft', id }) });
        cache.gc();
      },

      onLockChanged: (id, isLocked) => setDraftLocked(cache, id, isLocked),

      // Whatever happened while the socket was down never arrived. The queue
      // comes back as its first page, since a refetch cannot know how far the
      // reviewer had scrolled.
      onReconnected: () => {
        client
          .refetchQueries({
            // Only what is mounted: the preview's query is not when nothing is
            // selected, and naming it anyway logs a warning on every reconnect.
            include: 'active',
            onQueryUpdated: (query) =>
              resyncedQueries.has(query.queryName ?? ''),
          })
          .catch(() => undefined);
      },
    });
  }, [subscribe, client]);
}
